/**
 * <snk-modele> — sélecteur modèle + pointure pour Horizon.
 *
 * Modèle : on coche les options de la variante dans le sélecteur natif caché et on émet
 * « change » ; Horizon fait le reste (prix, galerie, stock, bouton, URL ?variant=).
 * Pointure : un champ caché properties[Pointure] dans chaque formulaire d'achat de la section,
 * et un garde qui bloque l'ajout tant qu'aucune pointure n'est choisie.
 */

const FOOT_ADVICE = {
  normal: { step: 0, text: 'Ta taille habituelle est la bonne. La paire va te chausser parfaitement.' },
  large: { step: 0.5, text: 'Ce modèle taille juste : prends une demi-pointure au-dessus pour être parfaitement à l’aise.' },
  fin: { step: 0, text: 'Reste sur ta taille habituelle : la coupe de ce modèle convient bien aux pieds fins.' },
};

class SnkModele extends HTMLElement {
  #size = '';
  #answers = {};
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.dialogs = Object.fromEntries([...this.querySelectorAll('dialog[data-dialog]')].map((d) => [d.dataset.dialog, d]));
    this.sizes = [...this.querySelectorAll('.snk-size')].map((b) => b.dataset.size);

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    for (const dialog of Object.values(this.dialogs)) {
      // un clic sur le fond (hors contenu) ferme la fenêtre
      dialog.addEventListener('click', (event) => event.target === dialog && dialog.close(), { signal });
      dialog.addEventListener('close', () => this.#lockScroll(), { signal });
    }

    // le garde passe avant les écouteurs délégués de Horizon (posés sur document)
    window.addEventListener('click', (event) => this.#guard(event), { capture: true, signal });
    window.addEventListener('submit', (event) => this.#guard(event), { capture: true, signal });

    this.#syncForms();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get section() {
    return this.closest('.shopify-section') ?? document;
  }

  /* ---------- fenêtres ---------- */

  #open(name) {
    for (const [key, dialog] of Object.entries(this.dialogs)) if (key !== name && dialog.open) dialog.close();
    const dialog = this.dialogs[name];
    if (!dialog) return;
    if (name === 'quiz') this.#resetQuiz();
    dialog.showModal();
    this.#lockScroll();
  }

  #lockScroll() {
    // Horizon verrouille le défilement par l'attribut scroll-lock (sa page défile dans .page-wrapper)
    const anyOpen = Object.values(this.dialogs).some((d) => d.open);
    document.documentElement.toggleAttribute('scroll-lock', anyOpen);
    document.documentElement.style.overflow = anyOpen ? 'hidden' : '';
  }

  #onClick(event) {
    const target = event.target.closest('button');
    if (!target) return;

    if (target.dataset.open) return this.#open(target.dataset.open);
    if (target.hasAttribute('data-close')) return target.closest('dialog')?.close();
    if (target.classList.contains('snk-model')) return this.#pickModel(target);
    if (target.classList.contains('snk-size')) {
      this.#setSize(target.dataset.size);
      return this.dialogs.sizes.close();
    }
    if (target.dataset.answer !== undefined) return this.#answer(target);
    if (target.dataset.go) return this.#go(target.dataset.go);
    if (target.hasAttribute('data-pick-result')) {
      this.#setSize(target.dataset.size);
      this.dialogs.quiz.close();
    }
  }

  /* ---------- modèle = variante native ---------- */

  #pickModel(card) {
    if (card.disabled) return;
    const options = JSON.parse(card.dataset.options || '[]');
    const picker = this.querySelector('variant-picker');
    if (!picker) return;

    let changed = null;
    picker.querySelectorAll('fieldset').forEach((fieldset, index) => {
      const input = [...fieldset.querySelectorAll('input[type="radio"]')].find((i) => i.value === options[index]);
      if (input && !input.checked) {
        input.checked = true;
        changed = input;
      }
    });
    picker.querySelectorAll('select').forEach((select, index) => {
      if (options[index] !== undefined && select.value !== options[index]) {
        select.value = options[index];
        changed = select;
      }
    });
    changed?.dispatchEvent(new Event('change', { bubbles: true }));

    this.querySelectorAll('.snk-model.is-on').forEach((c) => c.classList.remove('is-on'));
    card.classList.add('is-on');
    this.querySelector('[data-model-name]').textContent = card.querySelector('.snk-model__name').textContent;
    const img = card.querySelector('img');
    const thumb = this.querySelector('[data-model-thumb]');
    if (img && thumb) thumb.src = img.currentSrc || img.src;
    this.dialogs.model.close();
  }

  /* ---------- pointure ---------- */

  #forms() {
    return [...this.section.querySelectorAll('form[action*="/cart/add"]')].filter((f) => !f.closest('product-card, quick-add-dialog'));
  }

  #syncForms() {
    const name = `properties[${this.dataset.sizeProperty || 'Pointure'}]`;
    for (const form of this.#forms()) {
      let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
      if (!input) {
        input = Object.assign(document.createElement('input'), { type: 'hidden', name });
        form.append(input);
      }
      input.value = this.#size;
    }
  }

  #setSize(size) {
    if (!this.sizes.includes(String(size))) return;
    this.#size = String(size);
    this.querySelectorAll('.snk-size').forEach((b) => b.classList.toggle('is-on', b.dataset.size === this.#size));
    const label = this.querySelector('[data-size-value]');
    label.textContent = `EU ${this.#size}`;
    label.classList.remove('is-empty');
    this.querySelector('[data-error]').hidden = true;
    this.#syncForms();
  }

  #guard(event) {
    if (!this.isConnected) return;
    const inSection = (el) => el && this.section.contains(el) && !el.closest('product-card, quick-add-dialog');
    let hit = null;
    if (event.type === 'submit') hit = event.target.matches?.('form[action*="/cart/add"]') ? event.target : null;
    else hit = event.target.closest?.('[name="add"], add-to-cart-component button, shopify-accelerated-checkout, .shopify-payment-button');
    // la barre d'achat collante vit hors du bloc mais dans la section
    if (!hit && event.type === 'click') hit = event.target.closest?.('sticky-add-to-cart button');
    if (!inSection(hit)) return;

    this.#syncForms();
    if (this.#size) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.querySelector('[data-error]').hidden = false;
    this.#open('sizes');
  }

  /* ---------- conseiller de taille ---------- */

  #resetQuiz() {
    this.#answers = {};
    const quiz = this.dialogs.quiz;
    quiz.querySelectorAll('[data-answer].is-on').forEach((b) => b.classList.remove('is-on'));
    quiz.querySelectorAll('[data-step] [data-go]:not([data-go="gender"])').forEach((b) => (b.disabled = true));
    this.#show('intro');
  }

  #show(step) {
    this.dialogs.quiz.querySelectorAll('[data-step]').forEach((s) => (s.hidden = s.dataset.step !== step));
  }

  #answer(button) {
    const step = button.closest('[data-step]');
    step.querySelectorAll('[data-answer].is-on').forEach((b) => b.classList.remove('is-on'));
    button.classList.add('is-on');
    this.#answers[step.dataset.step] = button.dataset.answer;
    const next = step.querySelector('[data-go]');
    if (next) next.disabled = false;
  }

  #go(step) {
    if (step === 'result') return this.#result();
    this.#show(step);
  }

  #result() {
    const advice = FOOT_ADVICE[this.#answers.foot] ?? FOOT_ADVICE.normal;
    const wanted = parseFloat(this.#answers.usual) + advice.step;
    const size = this.sizes.reduce((best, s) => (Math.abs(parseFloat(s) - wanted) < Math.abs(parseFloat(best) - wanted) ? s : best), this.sizes[0]);
    const quiz = this.dialogs.quiz;
    quiz.querySelector('[data-result-size]').textContent = size;
    quiz.querySelector('[data-result-message]').textContent = advice.text;
    quiz.querySelector('[data-pick-result]').dataset.size = size;
    this.#show('result');
  }
}

if (!customElements.get('snk-modele')) customElements.define('snk-modele', SnkModele);
